import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { getAllParticipants, clearAllParticipants } from '../../../services/participantService';

const ParticipantList = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clearing, setClearing] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);

  useEffect(() => {
    loadParticipants();
  }, []);

  const loadParticipants = async () => {
    try {
      const { data, error } = await getAllParticipants();
      if (error) {
        console.error('Error loading participants:', error);
      } else if (data) {
        setParticipants(data);
      }
    } catch (error) {
      console.error('Error loading participants:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClearParticipants = async () => {
    if (!confirmClear) {
      setConfirmClear(true);
      return;
    }
    setClearing(true);
    try {
      const { error } = await clearAllParticipants();
      if (error) {
        console.error('Error clearing participants:', error);
      } else {
        setParticipants([]);
      }
    } catch (error) {
      console.error('Error clearing participants:', error);
    } finally {
      setClearing(false);
      setConfirmClear(false);
    }
  };

  const filteredParticipants = participants?.filter(participant =>
    participant?.instagramUsername?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
    participant?.contentType?.toLowerCase()?.includes(searchQuery?.toLowerCase())
  );

  const exportToCSV = () => {
    const csvContent = [
      ['Kullanıcı Adı'],
      ...participants?.map(p => [p?.instagramUsername])
    ]?.map(row => row?.join(','))?.join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `katilimcilar_${new Date()?.toLocaleDateString('tr-TR')}.csv`;
    link?.click();
  };

  if (loading) {
    return (
      <div className="card text-center py-12">
        <Icon name="Loader" size={48} color="var(--color-primary)" className="mx-auto mb-4 animate-spin" />
        <p className="text-muted-foreground">Katılımcılar yükleniyor...</p>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
            <Icon name="Users" size={20} color="var(--color-primary)" />
          </div>
          <div>
            <h3 className="text-lg md:text-xl font-heading font-semibold text-foreground">
              Katılımcı Listesi
            </h3>
            <p className="caption text-muted-foreground">
              Toplam {participants?.length} katılımcı
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="default"
            onClick={exportToCSV}
            iconName="Download"
            iconPosition="left"
          >
            CSV İndir
          </Button>
          <Button
            variant={confirmClear ? 'destructive' : 'outline'}
            size="default"
            onClick={handleClearParticipants}
            iconName={clearing ? 'Loader' : 'Trash2'}
            iconPosition="left"
            disabled={clearing}
          >
            {clearing ? 'Temizleniyor...' : confirmClear ? 'Emin misiniz? Onayla' : 'Katılımcıları Temizle'}
          </Button>
          {confirmClear && (
            <button
              onClick={() => setConfirmClear(false)}
              className="text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              İptal
            </button>
          )}
        </div>
      </div>
      <div className="mb-6">
        <Input
          type="search"
          placeholder="Kullanıcı adı veya içerik tipi ara..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e?.target?.value)}
        />
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">
                Kullanıcı Adı
              </th>
              <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">
                Tarih
              </th>
              <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">
                Saat
              </th>
              <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">
                İçerik Tipi
              </th>
            </tr>
          </thead>
          <tbody>
            {filteredParticipants?.length > 0 ? (
              filteredParticipants?.map((participant) => (
                <tr key={participant?.id} className="border-b border-border hover:bg-muted/50 transition-colors">
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                        <Icon name="User" size={16} color="var(--color-primary)" />
                      </div>
                      <span className="text-sm font-medium text-foreground">
                        {participant?.instagramUsername}
                      </span>
                    </div>
                  </td>
                  <td className="py-3 px-4 text-sm text-foreground">
                    {new Date(participant?.date)?.toLocaleDateString('tr-TR')}
                  </td>
                  <td className="py-3 px-4 text-sm text-foreground">
                    {participant?.time}
                  </td>
                  <td className="py-3 px-4">
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-accent/10 text-accent">
                      {participant?.contentType}
                    </span>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="4" className="py-12 text-center">
                  <Icon name="Search" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-4" />
                  <p className="text-muted-foreground">
                    Arama kriterlerine uygun katılımcı bulunamadı
                  </p>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ParticipantList;